import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  ArrowRight,
  Calendar,
  Wallet,
  Sparkles,
  ShieldCheck,
  Users,
  ChefHat as ChefHatIcon,
  Check,
  Star,
  Clock,
  TrendingUp,
  Phone,
} from "lucide-react";
import { useState } from "react";
import { Image } from "@/components/ui/image";
import { Button } from "@/components/ui/button";
import { ChefHat } from "@/components/svg/ChefHat";
import { AvatarCircles } from "@/components/ui/avatar-circles";
import { createSEOHead } from "@/lib/seo";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/(public)/devenir-chef")({
  head: () =>
    createSEOHead({
      title: "Devenir chef HomeToq · Faites de votre cuisine votre métier",
      description:
        "Rejoignez la communauté HomeToq, fixez vos prix, choisissez vos disponibilités et cuisinez chez vos clients. Commission de 10%, aucun frais d'inscription.",
    }),
  component: RouteComponent,
});

const heroAvatars = Array.from(
  { length: 6 },
  (_, i) => `/ipx/f_webp,q_75,w_96/images/avatar_${i + 1}.webp`,
);

const PREREQUISITES = [
  {
    title: "Vous êtes déjà chef professionnel",
    body: "Chef privé, ex-restaurateur, traiteur, ou diplômé d'une école de cuisine avec au moins 2 ans d'expérience.",
  },
  {
    title: "Vous êtes en règle administrativement",
    body: "Statut auto-entrepreneur, entreprise individuelle ou société — vous facturez vos prestations en bonne et due forme.",
  },
  {
    title: "Vous avez votre propre matériel",
    body: "Mallette de couteaux, tabliers, ustensiles spécifiques. La cuisine de vos clients fournit les équipements de base.",
  },
  {
    title: "Vous êtes assuré",
    body: "Responsabilité civile professionnelle obligatoire. Nous pouvons vous orienter vers nos partenaires si besoin.",
  },
  {
    title: "Vous êtes mobile et autonome",
    body: "Permis B et véhicule fortement recommandés pour transporter votre matériel et intervenir dans votre zone.",
  },
];

const VALUE_PROPS = [
  {
    icon: Wallet,
    title: "Vous gardez 90% de vos revenus",
    body: "Une commission unique de 10% — pas de frais cachés, pas d'abonnement, pas de frais d'inscription.",
  },
  {
    icon: Calendar,
    title: "Vos disponibilités, vos règles",
    body: "Travaillez quand vous voulez. Définissez vos créneaux, vos tarifs et vos menus en quelques clics.",
  },
  {
    icon: Sparkles,
    title: "Votre vitrine, votre style",
    body: "Mettez en avant votre univers culinaire avec une page chef personnalisée et vos menus signature.",
  },
  {
    icon: ShieldCheck,
    title: "On gère le reste",
    body: "Paiement sécurisé via Stripe, mise en relation, support client, facturation — vous cuisinez, on s'occupe du reste.",
  },
];

const STEPS = [
  {
    number: "01",
    icon: ChefHatIcon,
    title: "Inscription en 5 minutes",
    body: "Créez votre profil, ajoutez vos plats signatures et vos disponibilités.",
  },
  {
    number: "02",
    icon: Phone,
    title: "Rendez-vous téléphonique",
    body: "Notre équipe vous appelle pour faire connaissance, vous présenter HomeToq et valider votre profil.",
  },
  {
    number: "03",
    icon: Check,
    title: "Validation & mise en ligne",
    body: "Profil approuvé : votre vitrine devient visible aux clients de votre zone d'intervention.",
  },
  {
    number: "04",
    icon: TrendingUp,
    title: "Vos premières commandes",
    body: "Recevez vos demandes de réservation et lancez-vous à votre rythme.",
  },
];

const TESTIMONIALS = [
  {
    name: "Sophie",
    city: "Paris",
    avatar: "/images/avatar_1.webp",
    quote:
      "En 6 mois sur HomeToq, j'ai pu quitter mon poste en restaurant et me consacrer à ma passion à temps plein. La liberté de choisir mes prestations change tout.",
    rating: 5,
  },
  {
    name: "Karim",
    city: "Lyon",
    avatar: "/images/avatar_2.webp",
    quote:
      "Je faisais déjà des prestations à domicile en parallèle. HomeToq m'a permis de doubler mon volume sans avoir à me soucier de la prospection.",
    rating: 5,
  },
  {
    name: "Léa",
    city: "Bordeaux",
    avatar: "/images/avatar_3.webp",
    quote:
      "Le support est top, la plateforme simple et les clients respectueux. Je recommande à tous les chefs qui veulent voler de leurs propres ailes.",
    rating: 5,
  },
];

const FAQ = [
  {
    q: "Quelle commission prélevez-vous ?",
    a: "10% sur chaque prestation, c'est tout. Aucun frais d'inscription, aucun abonnement.",
  },
  {
    q: "Combien de temps faut-il pour être validé ?",
    a: "Notre équipe étudie chaque candidature avec attention sous 48 heures ouvrées. Vous recevrez un email dès que votre profil est en ligne.",
  },
  {
    q: "Comment fonctionne le paiement ?",
    a: "Le client règle au moment de la réservation via Stripe. Vous recevez votre rémunération directement sur votre compte bancaire après la prestation.",
  },
  {
    q: "Suis-je couvert par une assurance ?",
    a: "Vous restez responsable de votre activité. Nous vous recommandons fortement une assurance responsabilité civile professionnelle. Nous pouvons vous orienter vers nos partenaires.",
  },
  {
    q: "Combien puis-je espérer gagner ?",
    a: "Cela dépend de votre disponibilité, de vos tarifs et de votre ville. Nos chefs actifs réalisent en moyenne entre 4 et 12 prestations par mois, à des tarifs allant de 45€ à 180€ par convive.",
  },
  {
    q: "Quelles villes couvrez-vous ?",
    a: "HomeToq est actif dans toutes les grandes villes françaises. Vous définissez votre zone d'intervention lors de votre inscription.",
  },
];

function RouteComponent() {
  return (
    <div className="bg-cream/30">
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream/40 pt-10 lg:pt-16 pb-20 lg:pb-28">
        <div className="container mx-auto px-4 lg:px-6 max-w-6xl relative">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left — copy éditoriale */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col gap-7 order-2 lg:order-1"
            >
              <h1 className="font-h1 text-dark-blue leading-[1.05]">
                Cuisinez{" "}
                <span className="text-primary">chez vos clients.</span>
                <span className="block font-h2 font-medium text-dark-blue/60 mt-3">
                  Sans restau. Sans patron.
                  <br className="max-md:hidden" /> Sans limites.
                </span>
              </h1>

              <p className="font-p text-muted-foreground max-w-lg">
                HomeToq est la plateforme dédiée aux chefs privés en France.
                Fixez vos prix, composez vos menus, choisissez vos prestations
                — on s'occupe du reste.
              </p>

              {/* Stats inline (ticker) */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-dark-blue py-3 border-y border-dark-blue/10 w-fit">
                <span className="inline-flex items-baseline gap-1.5">
                  <strong className="font-h3 tabular-nums">10%</strong>
                  <span className="font-small text-muted-foreground">
                    de commission
                  </span>
                </span>
                <span className="text-dark-blue/20">•</span>
                <span className="inline-flex items-baseline gap-1.5">
                  <strong className="font-h3 tabular-nums">150+</strong>
                  <span className="font-small text-muted-foreground">
                    chefs actifs
                  </span>
                </span>
                <span className="text-dark-blue/20">•</span>
                <span className="inline-flex items-baseline gap-1.5">
                  <strong className="font-h3 tabular-nums">48h</strong>
                  <span className="font-small text-muted-foreground">
                    pour démarrer
                  </span>
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/inscription" search={{ role: "chef" }}>
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-white font-button-large px-7 py-6 rounded-lg gap-2 shadow-sm"
                  >
                    S'inscrire maintenant
                    <ArrowRight size={16} />
                  </Button>
                </Link>
                <a
                  href="#comment-ca-marche"
                  className="font-button-medium text-dark-blue hover:text-primary transition-colors px-4 py-3"
                >
                  Comment ça marche ?
                </a>
              </div>
            </motion.div>

            {/* Right — collage 3 photos asymétrique + badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative order-1 lg:order-2 h-[420px] lg:h-[520px]"
            >
              {/* Photo 1 — grande, centre-haut */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, rotate: -3 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="absolute top-0 right-2 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-white"
              >
                <Image
                  src="/images/find_chef_image.webp"
                  alt="Chef HomeToq en cuisine"
                  layout="fullWidth"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </motion.div>

              {/* Photo 2 — moyenne, en bas à gauche */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, rotate: 5 }}
                transition={{ duration: 0.5, delay: 0.35 }}
                className="absolute bottom-4 left-0 w-[52%] aspect-square rounded-2xl overflow-hidden shadow-xl border-4 border-white"
              >
                <Image
                  src="/images/menu_3.webp"
                  alt="Plat signature"
                  layout="fullWidth"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </motion.div>

              {/* Photo 3 — petite, milieu droit */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, rotate: 2 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute bottom-12 right-0 w-[38%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg border-4 border-white max-md:hidden"
              >
                <Image
                  src="/images/menu_5.webp"
                  alt="Service à domicile"
                  layout="fullWidth"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </motion.div>

              {/* Note + avatars en bas */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -bottom-6 right-2 bg-white rounded-2xl shadow-xl border border-border px-4 py-3 flex items-center gap-3"
              >
                <AvatarCircles
                  avatarUrls={heroAvatars.slice(0, 4)}
                  numPeople={150}
                  className="w-fit"
                />
                <div className="border-l border-border pl-3">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <Star
                        key={idx}
                        size={11}
                        className="text-primary"
                        fill="currentColor"
                      />
                    ))}
                  </div>
                  <p className="font-small text-muted-foreground mt-0.5">
                    <strong className="text-dark-blue">4.8/5</strong> · 150+ chefs
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="container mx-auto px-4 lg:px-6 max-w-6xl py-16 lg:py-24">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-2">
            Pourquoi HomeToq
          </p>
          <h2 className="font-h2 text-dark-blue">
            La plateforme pensée par et pour les chefs
          </h2>
        </motion.header>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUE_PROPS.map((prop, i) => {
            const Icon = prop.icon;
            return (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-border p-6 hover:border-primary/30 hover:shadow-lg transition-all flex flex-col gap-3"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-bold text-dark-blue text-base leading-tight">
                  {prop.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {prop.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PRÉREQUIS */}
      <section className="bg-white py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-6 max-w-5xl">
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 max-w-2xl mx-auto"
          >
            <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-2">
              Le profil que nous recherchons
            </p>
            <h2 className="font-h2 text-dark-blue">
              HomeToq est une plateforme curée
            </h2>
            <p className="text-muted-foreground mt-3">
              Pour garantir une qualité irréprochable à nos clients, nous
              sélectionnons des chefs déjà professionnels. Voici ce que nous
              attendons :
            </p>
          </motion.header>

          <div className="grid sm:grid-cols-2 gap-4">
            {PREREQUISITES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="flex items-start gap-3 p-5 rounded-2xl border border-border bg-cream/30 hover:border-primary/30 transition-all"
              >
                <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <Check className="w-4 h-4 text-primary" strokeWidth={3} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-dark-blue text-sm leading-tight mb-1">
                    {p.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-8 italic">
            Vous remplissez ces conditions ? Lancez-vous, l'inscription prend 5
            minutes.
          </p>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section
        id="comment-ca-marche"
        className="bg-cream/30 py-16 lg:py-24 scroll-mt-24"
      >
        <div className="container mx-auto px-4 lg:px-6 max-w-6xl">
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 max-w-2xl mx-auto"
          >
            <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-2">
              En 3 étapes simples
            </p>
            <h2 className="font-h2 text-dark-blue">
              De votre inscription à votre première presta
            </h2>
            <p className="text-muted-foreground mt-3">
              Zéro paperasse, un process clair et un accompagnement humain
              à chaque étape.
            </p>
          </motion.header>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className="relative bg-cream/40 rounded-2xl border border-border p-6 lg:p-8 flex flex-col gap-3"
                >
                  <span className="text-5xl font-bold text-primary/20 leading-none">
                    {step.number}
                  </span>
                  <div className="absolute top-6 right-6 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-dark-blue text-lg leading-tight mt-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="container mx-auto px-4 lg:px-6 max-w-6xl py-16 lg:py-24">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-2">
            Ils ont sauté le pas
          </p>
          <h2 className="font-h2 text-dark-blue">
            Ce que disent nos chefs
          </h2>
        </motion.header>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="bg-white rounded-2xl border border-border p-6 flex flex-col gap-4 hover:shadow-lg transition-all"
            >
              <div className="flex gap-0.5">
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <Star
                    key={idx}
                    size={14}
                    className="text-primary"
                    fill="currentColor"
                  />
                ))}
              </div>
              <blockquote className="text-sm text-dark-blue/85 leading-relaxed italic flex-1">
                « {t.quote} »
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-3 border-t border-border">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-cream shrink-0">
                  <Image
                    src={t.avatar}
                    alt={t.name}
                    layout="constrained"
                    width={40}
                    height={40}
                    className="object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-dark-blue leading-tight">
                    {t.name}
                  </p>
                  <p className="text-xs text-muted-foreground">{t.city}</p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-6 max-w-3xl">
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-2">
              Questions fréquentes
            </p>
            <h2 className="font-h2 text-dark-blue">Tout ce que vous voulez savoir</h2>
          </motion.header>

          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="container mx-auto px-4 lg:px-6 max-w-4xl py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl bg-dark-blue p-10 lg:p-16 text-white text-center"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-salmon/20 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-5 backdrop-blur-sm">
              <ChefHat className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-h2 mb-4">Prêt à rejoindre l'aventure ?</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              5 minutes pour s'inscrire. 48h pour être validé. Aucun engagement,
              aucun frais d'inscription.
            </p>
            <Link to="/inscription" search={{ role: "chef" }}>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6 rounded-xl gap-2 shadow-lg"
              >
                S'inscrire maintenant
                <ArrowRight size={16} />
              </Button>
            </Link>

            <div className="flex items-center justify-center gap-6 mt-8 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} />
                5 min
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users size={12} />
                +150 chefs
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={12} />
                Paiement sécurisé
              </span>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={cn(
        "bg-cream/40 border border-border rounded-2xl overflow-hidden transition-all",
        open && "border-primary/30",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-cream/60 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-dark-blue text-sm lg:text-base">
          {q}
        </span>
        <span
          className={cn(
            "shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center transition-transform",
            open && "rotate-45",
          )}
        >
          <span className="text-primary font-bold text-lg leading-none">+</span>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}
